import { useLocation } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { Price } from 'hb-react/shared/apps/Events/components/Price';
import { ITEMS_TYPES } from 'hb-react/shared/apps/Events/constants/itemsTypes';
import { useDonationsContext } from 'hb-react/shared/apps/Events/contexts/DonationsContext';
import { useEventCartContext } from 'hb-react/shared/apps/Events/contexts/EventCartContext';

const ShoppingCardLine = ({ item }) => {
  const { type } = item;

  if (type === ITEMS_TYPES.TICKET) {
    return <Ticket ticket={item} />;
  }
  if (type === ITEMS_TYPES.STANDALONE_ADDON) {
    return <Addon addon={item} />;
  }
  if (type === ITEMS_TYPES.DONATION) {
    return <Donation donation={item} />;
  }
  if (type === ITEMS_TYPES.PROMO_CODE) {
    return <PromoCode promoCode={item} />;
  }

  return null;
};

export default ShoppingCardLine;

const Line = ({ quantity, name, price, currency, mb, ...boxProps }) => (
  <Box justifyContent="space-between" alignItems="center" flexGrow={1} mb={mb}>
    <Box fontSize={1} {...boxProps}>
      {quantity && <>{quantity} x</>} {name}
    </Box>
    <Box fontSize={1} textAlign="right">
      <Price price={price * (quantity || 1)} currency={currency} />
    </Box>
  </Box>
);

const Ticket = ({ ticket }) => {
  const { addons } = ticket;

  return (
    <Box key={ticket.id} mb={4} flexDirection="column">
      <Line
        quantity={ticket.quantity}
        name={ticket.name}
        price={ticket.price}
        currency={ticket.currency}
        fontWeight={1}
        mb={2}
      />
      {addons &&
        addons.length > 0 &&
        addons.map((addon, i) => (
          <Line
            key={`${ticket.id}-${addon.id}`}
            quantity={addon.quantity}
            name={addon.name}
            price={addon.price}
            currency={addon.currency}
            mb={i + 1 === addons.length ? 0 : 2}
          />
        ))}
    </Box>
  );
};

const Addon = ({ addon }) => (
  <Line
    quantity={addon.quantity}
    name={addon.name}
    price={addon.price}
    currency={addon.currency}
    fontWeight={1}
    mb={4}
  />
);

const PromoCodeName = ({ promoCode }) => {
  const { removeItem } = useEventCartContext();
  const { pathname } = useLocation();

  const isDestroyable = pathname.includes('registration');

  return (
    <>
      {promoCode.name}
      {isDestroyable && (
        <Button
          dataTestId={`delete-card-item-${promoCode.id}`}
          icon={() => (
            <FontAwesomeIcon
              height="16px"
              iconName="xmark"
              iconType={CATEGORIES.SOLID}
            />
          )}
          ml={2}
          onClick={() => removeItem(promoCode.id)}
          size="small"
          variant="inversed"
        />
      )}
    </>
  );
};

const PromoCode = ({ promoCode }) => (
  <Line
    alignItems="center"
    currency={promoCode.currency}
    quantity={promoCode.quantity}
    fontWeight={1}
    mb={4}
    name={<PromoCodeName promoCode={promoCode} />}
    price={promoCode.price}
  />
);

const DonationName = ({ donation }) => {
  const { openModal, isEditable } = useDonationsContext();

  return (
    <>
      {donation.title}
      {isEditable && (
        <Button
          dataTestId={`edit-donation-${donation.id}`}
          icon={() => (
            <FontAwesomeIcon
              height="14px"
              iconName="pen"
              iconType={CATEGORIES.SOLID}
            />
          )}
          ml={2}
          onClick={openModal}
          size="small"
          variant="inversed"
        />
      )}
    </>
  );
};

const Donation = ({ donation }) => (
  <Line
    alignItems="center"
    currency={donation.currency}
    fontWeight={1}
    mb={4}
    name={<DonationName donation={donation} />}
    price={donation.amount}
  />
);
